import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, ShieldCheck, CheckCircle, X, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../config/api';
import { getRecaptchaSiteKey } from '../utils/apiConfig';

const Contact = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    topic: 'general', 
    message: '' 
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);
  const recaptchaRef = useRef(null);

  // Load reCAPTCHA site key dynamically
  useEffect(() => {
    const loadRecaptchaKey = async () => {
      const siteKey = await getRecaptchaSiteKey();
      if (siteKey) {
        setRecaptchaSiteKey(siteKey);
      } else {
        // Fallback to test key or env var
        setRecaptchaSiteKey(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI');
      }
    };
    loadRecaptchaKey();
  }, []);

  // Initialize reCAPTCHA
  useEffect(() => {
    if (!recaptchaSiteKey) return;

    const initRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render && document.getElementById('recaptcha-container')) {
        try {
          // Clear any existing CAPTCHA
          if (recaptchaRef.current) {
            window.grecaptcha.reset(recaptchaRef.current);
          }
          
          recaptchaRef.current = window.grecaptcha.render('recaptcha-container', {
            sitekey: recaptchaSiteKey,
            callback: (token) => {
              setCaptchaToken(token);
              setCaptchaError('');
            },
            'expired-callback': () => {
              setCaptchaToken(null);
              setCaptchaError('CAPTCHA expired. Please verify again.');
            },
            'error-callback': () => {
              setCaptchaToken(null);
              setCaptchaError('CAPTCHA error. Please try again.');
            },
          });
        } catch (error) {
          console.error('reCAPTCHA render error:', error);
        }
      }
    };

    // Wait for reCAPTCHA script to load
    if (window.grecaptcha && window.grecaptcha.render) {
      initRecaptcha();
    } else {
      // Retry after a short delay if script hasn't loaded
      const timer = setTimeout(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          initRecaptcha();
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [recaptchaSiteKey]);

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) {
      return 'Name is required';
    }
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (name.trim().length > 100) {
      return 'Name must be less than 100 characters';
    }
    if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateMessage = (message) => {
    if (!message.trim()) {
      return 'Message is required';
    }
    if (message.trim().length < 10) {
      return 'Message must be at least 10 characters';
    }
    if (message.trim().length > 5000) {
      return 'Message must be less than 5000 characters';
    }
    return '';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return validateName(value);
      case 'email':
        return validateEmail(value);
      case 'message':
        return validateMessage(value);
      default:
        return '';
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    
    // Real-time validation for touched fields
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.name = validateName(formData.name);
    newErrors.email = validateEmail(formData.email);
    newErrors.message = validateMessage(formData.message);
    
    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      message: true,
    });

    return !newErrors.name && !newErrors.email && !newErrors.message;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    // Validate CAPTCHA
    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA verification');
      toast.error('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/contact', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        topic: formData.topic,
        message: formData.message.trim(),
        captchaToken: captchaToken,
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Thank you! We\'ll get back to you within one business day.');
        
        // Reset form
        setFormData({ name: '', email: '', topic: 'general', message: '' });
        setErrors({});
        setTouched({});
        setCaptchaToken(null);
        setCaptchaError('');
        
        // Reset CAPTCHA
        if (recaptchaRef.current && window.grecaptcha) {
          window.grecaptcha.reset(recaptchaRef.current);
        }
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
        
        // Handle CAPTCHA errors
        if (error.response.data.message.includes('CAPTCHA')) {
          setCaptchaError(error.response.data.message);
          if (recaptchaRef.current && window.grecaptcha) {
            window.grecaptcha.reset(recaptchaRef.current);
          }
          setCaptchaToken(null);
        }
      } else if (error.response?.status === 429) {
        toast.error('Too many submissions. Please try again later.');
      } else {
        toast.error('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldStatus = (fieldName) => {
    if (!touched[fieldName]) return '';
    return errors[fieldName] ? 'error' : 'success';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 lg:py-20">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-700 font-semibold shadow">
              <Sparkles className="h-4 w-4" />
              We're here for every story beat
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-6">
              Contact mySTORYme
            </h1>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl border border-purple-100 shadow-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Send us a note</h2>
              <p className="text-gray-600 mb-8">
                We reply to most messages within one business day. Include context so we can route your request to the right
                storyteller, engineer, or privacy specialist.
              </p>
              <form className="space-y-5" onSubmit={handleSubmit}>
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Avery Bennett"
                      className={`w-full rounded-xl border-2 px-4 py-3 transition-all ${
                        getFieldStatus('name') === 'error'
                          ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                          : getFieldStatus('name') === 'success'
                          ? 'border-green-400 bg-green-50 focus:border-green-500 focus:ring-2 focus:ring-green-100'
                          : 'border-purple-100 focus:border-purple-400 focus:ring-2 focus:ring-purple-100'
                      }`}
                    />
                    {getFieldStatus('name') === 'success' && (
                      <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                    )}
                    {getFieldStatus('name') === 'error' && (
                      <X className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {touched.name && errors.name && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="you@email.com"
                      className={`w-full rounded-xl border-2 px-4 py-3 transition-all ${
                        getFieldStatus('email') === 'error'
                          ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                          : getFieldStatus('email') === 'success'
                          ? 'border-green-400 bg-green-50 focus:border-green-500 focus:ring-2 focus:ring-green-100'
                          : 'border-purple-100 focus:border-purple-400 focus:ring-2 focus:ring-purple-100'
                      }`}
                    />
                    {getFieldStatus('email') === 'success' && (
                      <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                    )}
                    {getFieldStatus('email') === 'error' && (
                      <X className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {touched.email && errors.email && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Topic Field */}
                <div>
                  <label htmlFor="topic" className="block text-sm font-semibold text-gray-700 mb-2">
                    Topic
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    value={formData.topic}
                    onChange={handleChange}
                    className="w-full rounded-xl border-2 border-purple-100 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 px-4 py-3 bg-white"
                  >
                    <option value="general">General question</option>
                    <option value="billing">Billing / subscription</option>
                    <option value="privacy">Privacy & data</option>
                    <option value="education">Schools / literacy programs</option>
                    <option value="press">Press & partnerships</option>
                  </select>
                </div>

                {/* Message Field */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Share as much context as you like..."
                      className={`w-full rounded-xl border-2 px-4 py-3 transition-all resize-none ${
                        getFieldStatus('message') === 'error'
                          ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                          : getFieldStatus('message') === 'success'
                          ? 'border-green-400 bg-green-50 focus:border-green-500 focus:ring-2 focus:ring-green-100'
                          : 'border-purple-100 focus:border-purple-400 focus:ring-2 focus:ring-purple-100'
                      }`}
                    />
                    {getFieldStatus('message') === 'success' && (
                      <CheckCircle className="absolute right-3 top-3 h-5 w-5 text-green-500" />
                    )}
                    {getFieldStatus('message') === 'error' && (
                      <X className="absolute right-3 top-3 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {touched.message && errors.message && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.message.length} / 5000 characters
                  </p>
                </div>

                {/* CAPTCHA */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Security Verification <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <div id="recaptcha-container" className="flex-1"></div>
                  </div>
                  {captchaError && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {captchaError}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    This helps us prevent automated abuse
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || Object.keys(errors).some(key => errors[key]) || !captchaToken}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send message
                    </>
                  )}
                </button>

                {/* Security Notice */}
                <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                  <ShieldCheck className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <p>
                    Your information is secure. We use industry-standard encryption to protect your data and will only use it to respond to your inquiry.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
